import { dailyTotals } from "../../lib/apps";
import { count, dateOnly } from "../../lib/format";
import type { AppCatalog, AppUsageDay } from "../../lib/types";
import { EmptyState } from "../ui/Feedback";
import { Cell, TableFrame, TableHead, TableRow } from "../ui/Table";
import type { Column } from "../ui/Table";
import { SectionTitle } from "../ui/Text";

/**
 * Thirty quota days of calls, newest first, one column per permission.
 *
 * The bar is answered calls against the busiest day shown, so a spike reads at a
 * glance; refused calls are their own column because they are the figure that
 * says a limit is set too low — or that a partner's client is misbehaving.
 */

export function AppUsage({
  usage,
  catalog,
}: {
  readonly usage: readonly AppUsageDay[];
  readonly catalog: AppCatalog;
}) {
  const days = dailyTotals(usage);
  const busiest = Math.max(1, ...days.map((day) => day.allowed));

  const columns: readonly Column[] = [
    { key: "day", label: "Day", width: "18%" },
    ...catalog.permissions.map((entry) => ({
      key: entry.key,
      label: entry.key,
      numeric: true,
      width: "16%",
    })),
    { key: "refused", label: "Refused", numeric: true, width: "12%" },
    { key: "bar", label: "" },
  ];

  return (
    <section aria-labelledby="app-usage" className="flex flex-col gap-4">
      <SectionTitle>
        <span id="app-usage">Last 30 days</span>
      </SectionTitle>

      {days.length === 0 ? (
        <EmptyState title="No calls yet." detail="Days with traffic appear here." />
      ) : (
        <TableFrame>
          <TableHead columns={columns} />
          <tbody>
            {days.map((day, index) => (
              <TableRow key={day.day} index={index} isFaulted={day.denied > 0}>
                <Cell>{dateOnly(day.day, catalog.timeZone)}</Cell>
                {catalog.permissions.map((entry) => (
                  <Cell key={entry.key} numeric muted={(day.byScope[entry.key] ?? 0) === 0}>
                    {count(day.byScope[entry.key] ?? 0)}
                  </Cell>
                ))}
                <Cell numeric muted={day.denied === 0}>
                  {count(day.denied)}
                </Cell>
                <Cell>
                  <span
                    aria-hidden="true"
                    className="block h-1.5 bg-ink-faint"
                    style={{ width: `${(day.allowed / busiest) * 100}%` }}
                  />
                </Cell>
              </TableRow>
            ))}
          </tbody>
        </TableFrame>
      )}
    </section>
  );
}
